import { Button, Form, Image, Input, Space } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect } from "react";
import CloseIcon from "../../../../../assets/icons/CloseIcon";
import placeholder from "../../../../../assets/images/placeholder.png";
import CustomModal from "../../../../../components/ui/CustomModal";
import { useFileUploadMutation } from "../../../../../features/superAdmin/employeeService/employeeManagementService";
import {
  useCreateProductMutation,
  useUpdateProductMutation,
} from "../../../../../features/superAdmin/ticketService/product/product.api";
import useError from "../../../../../hooks/useError";
import { toast } from "../../../../../lib/toast";
import { CustomModalProps } from "../../../../../types";
type Props = { data: any } & CustomModalProps;

const ManageProduct = ({ open, handleClose, data, ...rest }: Props) => {
  const [form] = Form.useForm();
  const isUpdating = !!data?.id;

  // ------ Watched values ------
  const logo = Form.useWatch("logo", form);

  // ------ Mutations ------
  const [
    uploadFile,
    { isLoading: uploading, isError: isUploadError, error: uploadError },
  ] = useFileUploadMutation();
  const [
    createProduct,
    {
      isLoading: creating,
      isError: isCreateError,
      error: createError,
      isSuccess: created,
    },
  ] = useCreateProductMutation();
  const [
    updateProduct,
    {
      isLoading: updating,
      isError: isUpdateError,
      error: updateError,
      isSuccess: updated,
    },
  ] = useUpdateProductMutation();

  // ------ Function for handle form finish ------
  const handleFinish = (values: any) => {
    if (isUpdating) {
      updateProduct({
        id: data?.id,
        data: values,
      });
    } else {
      createProduct(values);
    }
  };

  // ------ Function for handle file upload ------
  const handleFileUpload = async (file: File, key = "logo") => {
    const maxFileSize = 32 * 1024 * 1024; // 32MB
    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];

    if (!allowedTypes.includes(file.type)) {
      toast("error", `Only JPG and PNG image files are allowed.`);
      return;
    }

    if (file.size > maxFileSize) {
      toast("error", `"${file.name}" exceeds max file size of 32MB.`);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("name", file.name);

    try {
      const response: { Location?: string } = await uploadFile(
        formData,
      ).unwrap();

      if (response?.Location) {
        form.setFieldsValue({
          [key]: response.Location,
        });
      } else {
        console.warn("Upload succeeded but no file location was returned.");
      }
    } catch (error) {
      console.error("Image upload failed:", error);
    }
  };

  // ------ Function for handle image change ------
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    if (files.length === 0) return;

    const file = files[0]; // Only one image
    console.log(file);
    handleFileUpload(file);

    // Clear input value to allow re-selecting the same file
    e.target.value = "";
  };

  // ------ Function for handle image drug & drop ------
  const handleDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    const file = files[0];
    console.log(file);
    handleFileUpload(file);
  };

  // ------ Effects ------
  useEffect(() => {
    if (isUpdating) {
      form.setFieldsValue(data);
    }
  }, [data, isUpdating, form]);

  useEffect(() => {
    if ((created || updated) && !(creating || updating)) {
      handleClose();
      form.resetFields();
      toast(
        "success",
        isUpdating
          ? "Product updated successfully."
          : "Product created successfully.",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [created, updated, creating, updating]);

  // ------ Errors ------
  useError(isUploadError, uploadError, "File upload fail.");
  useError(isCreateError, createError, "Product create fail.");
  useError(isUpdateError, updateError, "Product update fail.");

  return (
    <CustomModal
      {...rest}
      width={400}
      open={open}
      handleClose={() => {
        handleClose();
        form.resetFields();
      }}
      title={isUpdating ? "Update Product" : "Add New Product"}
      footer={null}
    >
      <Form
        requiredMark={false}
        form={form}
        onFinish={handleFinish}
        layout="vertical"
      >
        <Form.Item
          rules={[{ required: true, message: "Please enter product title" }]}
          name="title"
          label="Product Title"
          className="mb-4"
        >
          <Input size="large" placeholder="Enter product title" />
        </Form.Item>
        <Form.Item
          name="description"
          label="Product Description"
          className="mb-4"
        >
          <TextArea size="large" placeholder="Enter product description" />
        </Form.Item>
        <Form.Item name="logo" label="Product Logo" className="mb-4">
          <div>
            {logo ? (
              <div className="relative inline-block">
                <Image
                  src={logo || placeholder}
                  height={100}
                  width={100}
                  preview={false}
                  className="rounded-md !object-contain border border-light border-dashed"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src = placeholder;
                  }}
                />
                <button
                  type="button"
                  className="absolute right-2 top-2 text-body-text"
                  onClick={() => form.resetFields(["logo"])}
                >
                  <CloseIcon height={10} width={10} />
                </button>
              </div>
            ) : (
              <label
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => handleDrop(e)}
                htmlFor="imageUpload"
                className={`h-[100px] w-full border border-dashed border-light rounded-md p-4 text-center text-display-medium cursor-pointer flex items-center justify-center ${
                  uploading ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                <div className={"text-center"}>
                  <span className="text-display-medium text-sm font-normal leading-normal">
                    Drag and Drop
                    <br />
                    Or
                    <br />
                  </span>
                  <span className="text-accent text-sm font-medium underline leading-normal">
                    Upload
                  </span>
                </div>
                <input
                  disabled={uploading}
                  id="imageUpload"
                  type="file"
                  accept="image/png, image/jpeg"
                  style={{ display: "none" }}
                  onChange={(e) => handleImageChange(e)}
                />
              </label>
            )}
          </div>
        </Form.Item>
        <Space className="flex justify-end items-center">
          <Button
            disabled={creating || updating}
            onClick={handleClose}
            htmlType="button"
            className="button-bordered-light"
          >
            Cancel
          </Button>
          <Button
            disabled={creating || updating}
            loading={creating || updating}
            htmlType="submit"
            className="button-primary-light"
          >
            {isUpdating ? "Update" : "Add"}
          </Button>
        </Space>
      </Form>
    </CustomModal>
  );
};

export default ManageProduct;
